import React, { useState, useEffect, useRef } from "react";
import {
  getAllAccesses,
  setActiveDatabase,
  redeemAccessCode,
  generateAccessCode,
  getPendingRequests,
  approveAccess,
  rejectAccess,
  updateAlias,
  getAccessCodes,
  revokeAccessCode,
  revokeUserAccess,
} from "../../api/accessManagementApi";
import { createOwnDatabase } from "../../api/passwordDatabaseApi";
import { sendRequest } from "../../services/dialogBridge";
import { OPS } from "../../services/messageOps";
import * as crypto from "../../utils/crypto";
import { useI18n } from "../../contexts/I18nContext";

/**
 * Zugriffsverwaltung Page (Page 3 - Access Management)
 * Sections:
 * 1. Active Database Card
 * 2. Join Database (Access Code)
 * 3. My Accesses Grid
 * 4. Owner: Share Database (Access Codes)
 * 5. Owner: Pending Requests
 */
export default function ZugriffsverwaltungPage({
  oid,
  passwortDatenbankId,
  masterKey,
  masterKeyVersion,
  isPaid,
  onNavigate,
  focusSectionRef,
  onActiveDbChanged,
  onConflictDetected,
}) {
  const { t, formatDateTime } = useI18n();
  // Data state
  const [accesses, setAccesses] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [generatedCodes, setGeneratedCodes] = useState([]);
  const [persistedAccessCodes, setPersistedAccessCodes] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [globalError, setGlobalError] = useState(null); // Only for page-wide critical errors
  const [joiningByCode, setJoiningByCode] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [loadingPersistedCodes, setLoadingPersistedCodes] = useState(false);
  const [revokingCodeId, setRevokingCodeId] = useState(null);
  const [expandedDbId, setExpandedDbId] = useState(null);
  const [highlightedDbId, setHighlightedDbId] = useState(null);
  const [revokingUserId, setRevokingUserId] = useState(null);

  // Section-specific alerts (success/error appear above related section)
  const [activeDbAlert, setActiveDbAlert] = useState({
    type: null,
    message: null,
  });
  const [joinByCodeAlert, setJoinByCodeAlert] = useState({
    type: null,
    message: null,
  });
  const [accessesAlert, setAccessesAlert] = useState({
    type: null,
    message: null,
  });
  const [shareDbAlert, setShareDbAlert] = useState({
    type: null,
    message: null,
  });
  const [pendingRequestsAlert, setPendingRequestsAlert] = useState({
    type: null,
    message: null,
  });

  // Form state - Join by code
  const [accessCodeInput, setAccessCodeInput] = useState("");
  const [joinAliasInput, setJoinAliasInput] = useState("");

  // Alias modal state
  const [editingAliasId, setEditingAliasId] = useState(null);
  const [newAliasValue, setNewAliasValue] = useState("");

  // Copy feedback state
  const [copiedCodeId, setCopiedCodeId] = useState(null);

  // Access codes filter toggle
  const [showAllAccessCodes, setShowAllAccessCodes] = useState(false);

  // Prevent double load
  const hasLoadedRef = useRef(false);

  // Track passwortDatenbankId to detect changes while mounted
  const lastDbIdRef = useRef(passwortDatenbankId);

  // Ref for Access Code input (used for programmatic focus)
  const accessCodeInputRef = useRef(null);

  // Handle focus hints from parent navigation
  useEffect(() => {
    const hint = focusSectionRef?.current;
    if (!hint) return;

    if (hint === "join-by-code") {
      focusSectionRef.current = null; // Consume the hint
      // Wait for DOM to render, then scroll and focus
      setTimeout(() => {
        const section = document.getElementById("join-by-code");
        if (section) {
          section.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        if (accessCodeInputRef.current) {
          accessCodeInputRef.current.focus();
        }
      }, 100);
    } else if (hint?.type === "focus-db" && hint.dbId) {
      focusSectionRef.current = null; // Consume the hint
      const targetDbId = hint.dbId;
      // Auto-expand and highlight the target row after data loads
      setExpandedDbId(targetDbId);
      setHighlightedDbId(targetDbId);
      // Scroll to the row after DOM renders
      setTimeout(() => {
        const row = document.getElementById(`db-row-${targetDbId}`);
        if (row) {
          row.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 300);
      // Clear highlight after 3 seconds
      setTimeout(() => setHighlightedDbId(null), 3000);
    }
  }, []); // Run once on mount (component mounts when page becomes active)

  // Helper to show section alert with auto-clear
  const showSectionAlert = (setter, type, message) => {
    setter({ type, message });
    setTimeout(() => setter({ type: null, message: null }), 8000);
  };

  // Auto-clear global error after 8 seconds
  useEffect(() => {
    if (globalError) {
      const timer = setTimeout(() => setGlobalError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [globalError]);

  // Load data on mount (only for paid users)
  useEffect(() => {
    if (oid && isPaid && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadData();
    }
  }, [oid, isPaid]);

  // Reload data when passwortDatenbankId changes (e.g. after DB delete/access end fallback)
  useEffect(() => {
    if (lastDbIdRef.current !== passwortDatenbankId && hasLoadedRef.current) {
      lastDbIdRef.current = passwortDatenbankId;
      loadData();
    }
  }, [passwortDatenbankId]);

  // Derived state
  const ownAccess = accesses.find((a) => a.rolle === "Owner");
  const ownDbId = ownAccess?.passwortDatenbankId;
  const activeAccess = accesses.find(
    (a) => a.passwortDatenbankId === passwortDatenbankId,
  );
  const isOwner = !!ownAccess;

  // --- Data Loading ---

  const loadData = async () => {
    setLoading(true);
    setGlobalError(null);
    try {
      const response = await getAllAccesses(oid);
      if (response.success) {
        const newAccesses = response.data || [];
        setAccesses(newAccesses);
        // Load pending requests and persisted access codes if user is owner
        const ownerEntry = newAccesses.find(
          (a) => a.rolle === "Owner",
        );
        if (ownerEntry) {
          await loadPendingRequests(ownerEntry.passwortDatenbankId);
          await loadPersistedAccessCodes(ownerEntry.passwortDatenbankId);
        }
        return newAccesses; // Return for caller to use updated data
      } else {
        setGlobalError(response.message || t("Fehler beim Laden der Zugriffe"));
        return null;
      }
    } catch (err) {
      console.error("Error loading accesses:", err);
      setGlobalError(t("Fehler beim Laden der Zugriffsdaten"));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRequests = async (dbId) => {
    try {
      const response = await getPendingRequests(oid, dbId);
      if (response.success) {
        setPendingRequests(response.data || []);
      }
    } catch (err) {
      console.error("Error loading pending requests:", err);
    }
  };

  const loadPersistedAccessCodes = async (dbId) => {
    setLoadingPersistedCodes(true);
    try {
      const response = await getAccessCodes(oid, dbId);
      if (response.success) {
        setPersistedAccessCodes(response.data || []);
      } else {
        console.error("Error loading access codes:", response.message);
      }
    } catch (err) {
      console.error("Error loading access codes:", err);
    } finally {
      setLoadingPersistedCodes(false);
    }
  };

  // --- Helpers ---

  function getDisplayName(access) {
    if (access.aliasName) return access.aliasName;
    if (access.rolle === "Owner") return t("Meine Passwortdatenbank");
    return t("Geteilte Passwortdatenbank");
  }

  function getShortId(id) {
    return id?.substring(0, 8) || "";
  }

  function formatDate(dateStr) {
    if (!dateStr) return "-";
    return formatDateTime(dateStr, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  // --- Actions ---

  /**
   * Decrypts MasterKey for the newly selected database via the sidebar bridge.
   * The sidebar holds the private key and performs the RSA decryption.
   * @returns {Promise<Uint8Array|null>} Decrypted MasterKey or null on failure
   */
  const decryptMasterKeyForActiveDb = async () => {
    try {
      const result = await sendRequest(OPS.DECRYPT_MASTER_KEY);
      if (!result?.masterKeyBase64) {
        console.error("[Crypto] Bridge returned no MasterKey");
        return null;
      }
      // Reconstruct Uint8Array from Base64
      const mkBytes = Uint8Array.from(atob(result.masterKeyBase64), c => c.charCodeAt(0));
      return mkBytes;
    } catch (err) {
      console.error("[Crypto] Error decrypting MasterKey via bridge:", err);
      return null;
    }
  };

  const handleSwitchToOwnDb = async () => {
    if (!ownDbId) return;
    setSavingId("switch-own");
    try {
      const response = await setActiveDatabase(oid, ownDbId);
      if (response.success) {
        // Decrypt MasterKey for the new active database
        const newMasterKey = await decryptMasterKeyForActiveDb();
        // Get active user count for the new database
        const newDbAccess = accesses.find(
          (a) => a.passwortDatenbankId === ownDbId,
        );
        const newUserCount = newDbAccess?.aktiveBenutzerAnzahl || 0;
        showSectionAlert(
          setActiveDbAlert,
          "success",
          t("Aktive Datenbank gewechselt"),
        );
        if (onActiveDbChanged)
          onActiveDbChanged(ownDbId, newMasterKey, newUserCount, true);
      } else {
        showSectionAlert(
          setActiveDbAlert,
          "error",
          response.message || t("Fehler beim Wechseln"),
        );
      }
    } catch (err) {
      showSectionAlert(
        setActiveDbAlert,
        "error",
        t("Fehler beim Wechseln der Datenbank"),
      );
    } finally {
      setSavingId(null);
    }
  };

  const handleCreateOwnDatabase = async () => {
    setSavingId("create-own-db");
    try {
      // Ask sidebar to generate + encrypt a new MasterKey
      const bridgeResult = await sendRequest(OPS.ENCRYPT_NEW_MASTER_KEY);
      if (!bridgeResult?.encryptedMasterKey) {
        showSectionAlert(
          setActiveDbAlert,
          "error",
          t("Schlüssel konnte nicht erstellt werden. Bitte entsperren Sie erneut."),
        );
        return;
      }

      // Call backend to create own DB
      const response = await createOwnDatabase({
        oid,
        encryptedMasterKey: bridgeResult.encryptedMasterKey,
        masterKeyEnvelope: bridgeResult.masterKeyEnvelope,
      });

      if (response.success) {
        showSectionAlert(
          setActiveDbAlert,
          "success",
          t("Eigene Passwortdatenbank erfolgreich erstellt."),
        );
        // Refresh accesses to pick up the new Owner entry
        const newAccesses = await loadData();
        if (newAccesses) {
          const newOwnerEntry = newAccesses.find((a) => a.rolle === "Owner");
          if (newOwnerEntry && onActiveDbChanged) {
            // Update ref to prevent useEffect from triggering a second loadData()
            lastDbIdRef.current = newOwnerEntry.passwortDatenbankId;
            // Switch active DB to the newly created one
            await setActiveDatabase(oid, newOwnerEntry.passwortDatenbankId);
            const decryptedKey = await decryptMasterKeyForActiveDb();
            onActiveDbChanged(
              newOwnerEntry.passwortDatenbankId,
              decryptedKey,
              1,
              true,
            );
          }
        }
      } else {
        showSectionAlert(
          setActiveDbAlert,
          "error",
          response.message || t("Fehler beim Erstellen der Datenbank"),
        );
      }
    } catch (err) {
      console.error("Error creating own database:", err);
      showSectionAlert(
        setActiveDbAlert,
        "error",
        t("Fehler beim Erstellen der Passwortdatenbank"),
      );
    } finally {
      setSavingId(null);
    }
  };

  const handleSetActive = async (dbId) => {
    setSavingId(dbId);
    try {
      const response = await setActiveDatabase(oid, dbId);
      if (response.success) {
        // Decrypt MasterKey for the new active database
        const newMasterKey = await decryptMasterKeyForActiveDb();
        // Get active user count for the new database
        const newDbAccess = accesses.find(
          (a) => a.passwortDatenbankId === dbId,
        );
        const newUserCount = newDbAccess?.aktiveBenutzerAnzahl || 0;
        showSectionAlert(
          setAccessesAlert,
          "success",
          t("Aktive Datenbank festgelegt"),
        );
        if (onActiveDbChanged)
          onActiveDbChanged(dbId, newMasterKey, newUserCount, dbId === ownDbId);
      } else {
        showSectionAlert(
          setAccessesAlert,
          "error",
          response.message || t("Fehler beim Festlegen"),
        );
      }
    } catch (err) {
      showSectionAlert(
        setAccessesAlert,
        "error",
        t("Fehler beim Festlegen der aktiven Datenbank"),
      );
    } finally {
      setSavingId(null);
    }
  };

  const handleJoinByCode = async (e) => {
    e.preventDefault();
    if (!accessCodeInput.trim()) {
      showSectionAlert(
        setJoinByCodeAlert,
        "error",
        t("Bitte geben Sie einen Access Code ein"),
      );
      return;
    }

    setJoiningByCode(true);
    try {
      // Send aliasName directly with the redeem request
      const aliasToSend = joinAliasInput.trim() || null;
      const response = await redeemAccessCode(
        oid,
        accessCodeInput.trim(),
        aliasToSend,
      );
      if (response.success) {
        showSectionAlert(
          setJoinByCodeAlert,
          "success",
          t("Anfrage gesendet. Bitte warten Sie auf Freigabe durch den Besitzer."),
        );
        setAccessCodeInput("");
        setJoinAliasInput("");
        // Reload accesses to show new pending entry
        await loadData();
      } else {
        showSectionAlert(
          setJoinByCodeAlert,
          "error",
          response.message || t("Fehler beim Einlösen des Access Codes"),
        );
      }
    } catch (err) {
      const msg =
        err.response?.data?.message || t("Fehler beim Einlösen des Access Codes");
      showSectionAlert(setJoinByCodeAlert, "error", msg);
    } finally {
      setJoiningByCode(false);
    }
  };

  const handleGenerateCode = async () => {
    if (!ownDbId) return;
    setGeneratingCode(true);
    try {
      const response = await generateAccessCode(oid, ownDbId);
      if (response.success && response.data) {
        const codeData = response.data;
        setGeneratedCodes((prev) => [
          {
            id: codeData.passwortDatenbankAccessCodeId,
            code: codeData.accessCode,
            erstelltAm: codeData.erstelltAm || new Date().toISOString(),
            gueltigBis: codeData.gueltigBis,
            status: codeData.status || t("Gültig"),
          },
          ...prev,
        ]);
        showSectionAlert(
          setShareDbAlert,
          "success",
          t("Access Code erstellt. Der Access Code wird nur jetzt angezeigt – bitte kopieren Sie ihn."),
        );
        // Refresh persisted codes to include the new one
        await loadPersistedAccessCodes(ownDbId);
      } else {
        showSectionAlert(
          setShareDbAlert,
          "error",
          response.message || t("Fehler beim Erstellen des Access Codes"),
        );
      }
    } catch (err) {
      showSectionAlert(
        setShareDbAlert,
        "error",
        t("Fehler beim Erstellen des Access Codes"),
      );
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleCopyCode = (codeId, code) => {
    navigator.clipboard
      .writeText(code)
      .then(() => {
        setCopiedCodeId(codeId);
        setTimeout(() => setCopiedCodeId(null), 2000);
      })
      .catch(() => {
        showSectionAlert(setShareDbAlert, "error", t("Kopieren fehlgeschlagen"));
      });
  };

  const handleRevokeCode = async (accessCodeId) => {
    if (!ownDbId) return;
    setRevokingCodeId(accessCodeId);
    try {
      const response = await revokeAccessCode(oid, accessCodeId);
      if (response.success) {
        showSectionAlert(setShareDbAlert, "success", t("Access Code widerrufen"));
        // Refresh persisted codes
        await loadPersistedAccessCodes(ownDbId);
      } else {
        showSectionAlert(
          setShareDbAlert,
          "error",
          response.message || t("Fehler beim Widerrufen des Access Codes"),
        );
      }
    } catch (err) {
      showSectionAlert(
        setShareDbAlert,
        "error",
        t("Fehler beim Widerrufen des Access Codes"),
      );
    } finally {
      setRevokingCodeId(null);
    }
  };

  const handleRevokeUserAccess = async (dbId, benutzerId) => {
    setRevokingUserId(benutzerId);
    try {
      const response = await revokeUserAccess(oid, dbId, benutzerId);
      if (response.success) {
        showSectionAlert(
          setAccessesAlert,
          "success",
          t("Zugriff erfolgreich entzogen."),
        );
        await loadData();
      } else {
        showSectionAlert(
          setAccessesAlert,
          "error",
          response.message || t("Fehler beim Entziehen des Zugriffs"),
        );
      }
    } catch (err) {
      console.error("Error revoking user access:", err);
      showSectionAlert(
        setAccessesAlert,
        "error",
        t("Fehler beim Entziehen des Zugriffs"),
      );
    } finally {
      setRevokingUserId(null);
    }
  };

  const handleApprove = async (request) => {
    if (!masterKey || !ownDbId) {
      showSectionAlert(
        setPendingRequestsAlert,
        "error",
        t("MasterKey nicht verfügbar. Bitte entsperren Sie zuerst die Datenbank."),
      );
      return;
    }
    setSavingId(request.passwortDatenbankBenutzerId);
    try {
      // Encrypt MasterKey with requester's public key
      const encryptedMasterKey = await crypto.encryptMasterKeyWithPublicKey(
        masterKey,
        request.benutzerPublicKey,
      );

      // Create MasterKeyEnvelope metadata (describes how MasterKey was encrypted)
      const masterKeyEnvelope = crypto.createMasterKeyEnvelope(1, masterKeyVersion || 1);

      const response = await approveAccess({
        ownerOid: oid,
        passwortDatenbankId: ownDbId,
        benutzerId: request.benutzerId,
        encryptedMasterKey: encryptedMasterKey,
        expectedMasterKeyVersion: masterKeyVersion || 1,
        masterKeyEnvelope: JSON.stringify(masterKeyEnvelope),
      });

      if (response.success) {
        showSectionAlert(
          setPendingRequestsAlert,
          "success",
          t("Zugriff freigegeben"),
        );
        // Remove from pending list
        setPendingRequests((prev) =>
          prev.filter(
            (r) =>
              r.passwortDatenbankBenutzerId !==
              request.passwortDatenbankBenutzerId,
          ),
        );
        // Reload accesses and get updated data
        const updatedAccesses = await loadData();

        // Notify parent about updated user count if active database is the owner's database
        if (onActiveDbChanged && passwortDatenbankId === ownDbId && updatedAccesses) {
          const ownerAccess = updatedAccesses.find(a => a.passwortDatenbankId === ownDbId);
          const newUserCount = ownerAccess?.aktiveBenutzerAnzahl || 0;
          onActiveDbChanged(passwortDatenbankId, masterKey, newUserCount, true);
        }
        // Scroll back to pending requests section
        setTimeout(() => {
          document.getElementById("pending-requests")?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else if (response.statusCode === 409) {
        // Concurrency conflict - delegate to parent modal with countdown
        if (onConflictDetected) {
          onConflictDetected();
        }
      } else {
        showSectionAlert(
          setPendingRequestsAlert,
          "error",
          response.message || t("Fehler beim Freigeben"),
        );
      }
    } catch (err) {
      console.error("Error approving access:", err);
      showSectionAlert(
        setPendingRequestsAlert,
        "error",
        t("Fehler beim Freigeben des Zugriffs"),
      );
    } finally {
      setSavingId(null);
    }
  };

  const handleReject = async (request) => {
    if (!ownDbId) return;
    setSavingId(request.passwortDatenbankBenutzerId);
    try {
      const response = await rejectAccess({
        ownerOid: oid,
        passwortDatenbankId: ownDbId,
        benutzerId: request.benutzerId,
      });
      if (response.success) {
        showSectionAlert(
          setPendingRequestsAlert,
          "success",
          t("Zugriff abgelehnt"),
        );
        setPendingRequests((prev) =>
          prev.filter(
            (r) =>
              r.passwortDatenbankBenutzerId !==
              request.passwortDatenbankBenutzerId,
          ),
        );
        // Scroll back to pending requests section
        setTimeout(() => {
          document.getElementById("pending-requests")?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        showSectionAlert(
          setPendingRequestsAlert,
          "error",
          response.message || t("Fehler beim Ablehnen"),
        );
      }
    } catch (err) {
      showSectionAlert(
        setPendingRequestsAlert,
        "error",
        t("Fehler beim Ablehnen des Zugriffs"),
      );
    } finally {
      setSavingId(null);
    }
  };

  const handleSaveAlias = async () => {
    if (!editingAliasId) return;
    setSavingId(editingAliasId);
    try {
      const aliasToSave = newAliasValue.trim() || null;
      const response = await updateAlias(editingAliasId, oid, aliasToSave);
      if (response.success) {
        showSectionAlert(
          setActiveDbAlert,
          "success",
          aliasToSave ? t("Alias gespeichert") : t("Alias entfernt"),
        );
        // Update local state
        setAccesses((prev) =>
          prev.map((a) =>
            a.passwortDatenbankBenutzerId === editingAliasId
              ? { ...a, aliasName: aliasToSave }
              : a,
          ),
        );
        setEditingAliasId(null);
        setNewAliasValue("");
      } else {
        showSectionAlert(
          setActiveDbAlert,
          "error",
          response.message || t("Fehler beim Speichern des Alias"),
        );
      }
    } catch (err) {
      showSectionAlert(
        setActiveDbAlert,
        "error",
        t("Fehler beim Speichern des Alias"),
      );
    } finally {
      setSavingId(null);
    }
  };

  const openAliasModal = (zugriffId, currentAlias) => {
    setEditingAliasId(zugriffId);
    setNewAliasValue(currentAlias || "");
  };

  // --- Free User Guard ---

  if (!isPaid) {
    return (
      <div>
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "16px" }}
        >
          {t("Zugriffsverwaltung")}
        </h2>
        <div
          style={{
            padding: "16px",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "6px",
            color: "#856404",
          }}
        >
          <p style={{ margin: 0 }}>
            {t("Das Teilen von Passwortdatenbanken ist Teil des Paid-Plans. Bitte aktivieren Sie eine Paid-Lizenz, um diese Funktion zu nutzen.")}
          </p>
        </div>
      </div>
    );
  }

  // --- Loading State ---

  if (loading) {
    return (
      <div>
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "16px" }}
        >
          {t("Zugriffsverwaltung")}
        </h2>
        <p style={{ color: "#666" }}>{t("Lade Zugriffsdaten...")}</p>
      </div>
    );
  }

  // --- Main Render ---

  return (
    <div>
      <h2 style={{ fontSize: "20px", fontWeight: "600", marginBottom: "16px" }}>
        {t("Zugriffsverwaltung")}
      </h2>

      {/* Global Error - only for page-wide critical events like initial load failure */}
      {globalError && (
        <div
          style={{
            padding: "12px",
            backgroundColor: "#f8d7da",
            border: "1px solid #f5c6cb",
            borderRadius: "4px",
            color: "#721c24",
            marginBottom: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{globalError}</span>
          <button
            onClick={() => setGlobalError(null)}
            style={{
              background: "none",
              border: "none",
              fontSize: "18px",
              cursor: "pointer",
              color: "#721c24",
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* SECTION 1: Active Database */}
      <Section
        title={t("Aktive Passwortdatenbank")}
        alert={activeDbAlert}
        onDismissAlert={() => setActiveDbAlert({ type: null, message: null })}
      >
        {activeAccess ? (
          <div>
            <div style={{ marginBottom: "8px" }}>
              <strong>{getDisplayName(activeAccess)}</strong>
              <span
                style={{ color: "#888", marginLeft: "8px", fontSize: "12px" }}
              >
                ID: {getShortId(activeAccess.passwortDatenbankId)}
              </span>
            </div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {ownDbId && passwortDatenbankId !== ownDbId && (
                <button
                  onClick={handleSwitchToOwnDb}
                  disabled={savingId === "switch-own"}
                  style={buttonStyle("secondary")}
                >
                  {savingId === "switch-own"
                  ? "..."
                  : t("Zu meiner Passwortdatenbank wechseln")}
                </button>
              )}
              {!ownDbId && (
                <button
                  onClick={handleCreateOwnDatabase}
                  disabled={savingId === "create-own-db"}
                  style={buttonStyle("secondary")}
                  title={t("Erstellt eine persönliche Passwortdatenbank und aktiviert sie direkt.")}
                >
                  {savingId === "create-own-db"
                    ? "..."
                    : t("Eigene Passwortdatenbank erstellen")}
                </button>
              )}
              <button
                onClick={() =>
                  openAliasModal(
                    activeAccess.passwortDatenbankBenutzerId,
                    activeAccess.aliasName,
                  )
                }
                style={buttonStyle("secondary")}
              >
                {t("Alias vergeben")}
              </button>
            </div>
          </div>
        ) : (
          <p style={{ color: "#666", margin: 0 }}>
            {t("Keine aktive Passwortdatenbank ausgewählt.")}
          </p>
        )}
      </Section>

      {/* SECTION 2: Join by Code */}
      <Section
        title={t("Einer Datenbank beitreten")}
        id="join-by-code"
        alert={joinByCodeAlert}
        onDismissAlert={() => setJoinByCodeAlert({ type: null, message: null })}
      >
        <form onSubmit={handleJoinByCode}>
          <div style={{ marginBottom: "12px" }}>
            <label style={labelStyle}>{t("Access Code")} *</label>
            <input
              ref={accessCodeInputRef}
              type="text"
              value={accessCodeInput}
              onChange={(e) => setAccessCodeInput(e.target.value)}
              placeholder={t("Access Code eingeben")}
              disabled={joiningByCode}
              style={inputStyle}
            />
          </div>
          <div style={{ marginBottom: "12px" }}>
            <label style={labelStyle}>
              {t("Alias-Name (optional, nur für mich)")}
            </label>
            <input
              type="text"
              value={joinAliasInput}
              onChange={(e) => setJoinAliasInput(e.target.value)}
              placeholder={t("z.B. Team-Passwörter")}
              disabled={joiningByCode}
              style={inputStyle}
              maxLength={100}
            />
          </div>
          <button
            type="submit"
            disabled={joiningByCode || !accessCodeInput.trim()}
            style={buttonStyle("primary")}
          >
            {joiningByCode ? t("Wird gesendet...") : t("Anfragen")}
          </button>
        </form>
      </Section>

      {/* SECTION 3: My Accesses */}
      <Section
        title={t("Meine Zugriffe")}
        alert={accessesAlert}
        onDismissAlert={() => setAccessesAlert({ type: null, message: null })}
      >
        {accesses.length === 0 ? (
          <p style={{ color: "#666", margin: 0 }}>{t("Keine Zugriffe vorhanden.")}</p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: "32px", padding: "8px 4px" }}></th>
                <th style={thStyle}>{t("Datenbank")}</th>
                <th style={{ ...thStyle, textAlign: "center" }}>{t("Zugriff")}</th>
                <th style={{ ...thStyle, textAlign: "center" }}>{t("Aktionen")}</th>
              </tr>
            </thead>
            <tbody>
              {accesses
                .filter((a) => a.status !== "Entzogen")
                .map((access) => {
                  const isActive =
                    access.passwortDatenbankId === passwortDatenbankId;
                  const canExpand =
                    access.isOwner && access.sharedUsers != null;
                  const isExpanded =
                    expandedDbId === access.passwortDatenbankId;
                  const isHighlighted =
                    highlightedDbId === access.passwortDatenbankId;
                  return (
                    <React.Fragment key={access.passwortDatenbankBenutzerId}>
                      <tr
                        id={`db-row-${access.passwortDatenbankId}`}
                        style={{
                          backgroundColor: isHighlighted
                            ? "#fff9c4"
                            : "transparent",
                          transition: "background-color 1s ease-out",
                        }}
                      >
                        <td style={{ ...tdStyle, padding: "8px 4px", textAlign: "center" }}>
                          {canExpand && (
                            <span
                              onClick={() =>
                                setExpandedDbId(
                                  isExpanded ? null : access.passwortDatenbankId,
                                )
                              }
                              style={{
                                cursor: "pointer",
                                fontSize: "14px",
                                userSelect: "none",
                                display: "inline-block",
                                transform: isExpanded
                                  ? "rotate(90deg)"
                                  : "rotate(0deg)",
                                transition: "transform 0.2s",
                              }}
                            >
                              &#9654;
                            </span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          <div>
                            <span style={{ fontWeight: "500" }}>
                              {getDisplayName(access)}
                            </span>
                            <br />
                            <span style={{ fontSize: "11px", color: "#888" }}>
                              {getShortId(access.passwortDatenbankId)}
                            </span>
                          </div>
                        </td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          <StatusBadge status={access.status} />
                        </td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          {access.status === "Freigegeben" && !isActive && (
                            <button
                              onClick={() =>
                                handleSetActive(access.passwortDatenbankId)
                              }
                              disabled={savingId === access.passwortDatenbankId}
                              style={buttonStyle("small")}
                            >
                              {savingId === access.passwortDatenbankId
                                ? "..."
                                : t("Als aktive DB festlegen")}
                            </button>
                          )}
                          {isActive && (
                            <span style={badgeStyle("#28a745", "#fff")}>
                              {t("Aktiv")}
                            </span>
                          )}
                          {access.status === "Ausstehend" && (
                            <span style={{ color: "#888", fontSize: "12px" }}>
                              {t("Wartet auf Freigabe")}
                            </span>
                          )}
                        </td>
                      </tr>
                      {/* Expandable shared users sub-area */}
                      {canExpand && isExpanded && (
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              padding: "0 8px 12px 40px",
                              borderBottom: "1px solid #eee",
                              backgroundColor: "#f9f9f9",
                            }}
                          >
                            <div style={{ padding: "12px 0" }}>
                              <strong
                                style={{
                                  fontSize: "12px",
                                  color: "#555",
                                  display: "block",
                                  marginBottom: "8px",
                                }}
                              >
                                {t("Benutzer mit Zugriff")}
                              </strong>
                              {access.sharedUsers.length === 0 ? (
                                <p
                                  style={{
                                    color: "#888",
                                    fontSize: "12px",
                                    margin: 0,
                                  }}
                                >
                                  {t("Keine weiteren Benutzer.")}
                                </p>
                              ) : (
                                <table style={subTableStyle}>
                                  <thead>
                                    <tr>
                                      <th style={subThStyle}>{t("Benutzer (E-Mail)")}</th>
                                      <th style={subThCenterStyle}>{t("Zugriff")}</th>
                                      <th style={subThCenterStyle}>{t("Rolle")}</th>
                                      <th style={subThCenterStyle}>{t("Aktion")}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {access.sharedUsers.map((user) => (
                                      <tr key={user.benutzerId}>
                                        <td style={subTdStyle}>
                                          {user.email}
                                        </td>
                                        <td style={subTdCenterStyle}>
                                          <StatusBadge status={user.status} />
                                        </td>
                                        <td style={subTdCenterStyle}>
                                          {user.rolle === "Owner" && (
                                            <span
                                              style={{
                                                fontSize: "11px",
                                                color: "#888",
                                                fontStyle: "italic",
                                              }}
                                            >
                                              {t("Besitzer")}
                                            </span>
                                          )}
                                        </td>
                                        <td style={subTdCenterStyle}>
                                          {user.rolle !== "Owner" &&
                                            user.status !== "Entzogen" ? (
                                              <button
                                                onClick={() =>
                                                  handleRevokeUserAccess(
                                                    access.passwortDatenbankId,
                                                    user.benutzerId,
                                                  )
                                                }
                                                disabled={
                                                  revokingUserId === user.benutzerId
                                                }
                                                style={buttonStyle("danger")}
                                              >
                                                {revokingUserId === user.benutzerId
                                                  ? "..."
                                                  : t("Zugriff entziehen")}
                                              </button>
                                            ) : (
                                              <span style={{ color: "#ccc" }}>—</span>
                                            )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
            </tbody>
          </table>
        )}
      </Section>

      {/* OWNER-ONLY SECTIONS */}
      {isOwner && (
        <>
          {/* SECTION 4: Share Database */}
          <Section
            title={t("Meine Datenbank teilen")}
            alert={shareDbAlert}
            onDismissAlert={() =>
              setShareDbAlert({ type: null, message: null })
            }
          >
            <button
              onClick={handleGenerateCode}
              disabled={generatingCode}
              style={buttonStyle("primary")}
            >
              {generatingCode ? t("Wird erstellt...") : t("Access Code erstellen")}
            </button>

            {generatedCodes.length > 0 && (
              <div style={{ marginTop: "16px" }}>
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "8px",
                  }}
                >
                  {t("Erstellte Access Codes")}
                </h4>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>{t("Code")}</th>
                      <th style={thStyle}>{t("Erstellt am")}</th>
                      <th style={thStyle}>{t("Gültig bis")}</th>
                      <th style={thStyle}>{t("Aktionen")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generatedCodes.map((entry) => (
                      <tr key={entry.id}>
                        <td style={tdStyle}>
                          <code
                            style={{
                              fontSize: "12px",
                              backgroundColor: "#f8f9fa",
                              padding: "2px 6px",
                              borderRadius: "3px",
                            }}
                          >
                            {entry.code}
                          </code>
                        </td>
                        <td style={tdStyle}>{formatDate(entry.erstelltAm)}</td>
                        <td style={tdStyle}>
                          {entry.gueltigBis
                            ? formatDate(entry.gueltigBis)
                            : "–"}
                        </td>
                        <td style={tdStyle}>
                          <button
                            onClick={() => handleCopyCode(entry.id, entry.code)}
                            style={
                              copiedCodeId === entry.id
                                ? buttonStyle("copied")
                                : buttonStyle("small")
                            }
                            disabled={copiedCodeId === entry.id}
                          >
                            {copiedCodeId === entry.id
                              ? t("Kopiert!")
                              : t("Kopieren")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p
                  style={{ fontSize: "11px", color: "#888", marginTop: "8px" }}
                >
                  {t("Hinweis: Access Codes sind standardmäßig 72 Stunden gültig und werden nur in dieser Sitzung im Klartext angezeigt.")}
                </p>
              </div>
            )}

            {/* Persisted Access Codes Grid */}
            <div style={{ marginTop: "24px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <h4 style={{ fontSize: "14px", fontWeight: "600", margin: 0 }}>
                  {t("Meine Access Codes")}
                </h4>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "12px",
                    color: "#666",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={showAllAccessCodes}
                    onChange={(e) => setShowAllAccessCodes(e.target.checked)}
                    style={{ cursor: "pointer" }}
                  />
                  {t("Auch inaktive Access Codes anzeigen")}
                </label>
              </div>
              {loadingPersistedCodes ? (
                <p style={{ color: "#666", fontSize: "13px" }}>
                  {t("Lade Access Codes...")}
                </p>
              ) : (
                (() => {
                  const filteredCodes = showAllAccessCodes
                    ? persistedAccessCodes
                    : persistedAccessCodes.filter(
                        (code) => code.status === "Gültig",
                      );

                  if (filteredCodes.length === 0) {
                    return (
                      <p style={{ color: "#666", fontSize: "13px", margin: 0 }}>
                        {showAllAccessCodes
                          ? t("Keine Access Codes vorhanden.")
                          : t("Keine gültigen Access Codes vorhanden.")}
                      </p>
                    );
                  }

                  return (
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={thStyle}>{t("Erstellt am")}</th>
                          <th style={thStyle}>{t("Gültig bis")}</th>
                          <th style={thStyle}>{t("Status")}</th>
                          <th style={thStyle}>{t("Verwendet am")}</th>
                          <th style={thStyle}>{t("Aktionen")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCodes.map((code) => (
                          <tr key={code.passwortDatenbankAccessCodeId}>
                            <td style={tdStyle}>
                              {formatDate(code.erstelltAm)}
                            </td>
                            <td style={tdStyle}>
                              {code.gueltigBis
                                ? formatDate(code.gueltigBis)
                                : "–"}
                            </td>
                            <td style={tdStyle}>
                              <AccessCodeStatusBadge status={code.status} />
                            </td>
                            <td style={tdStyle}>
                              {code.verwendetAm
                                ? formatDate(code.verwendetAm)
                                : "–"}
                            </td>
                            <td style={tdStyle}>
                              {code.status === "Gültig" ? (
                                <button
                                  onClick={() =>
                                    handleRevokeCode(
                                      code.passwortDatenbankAccessCodeId,
                                    )
                                  }
                                  disabled={
                                    revokingCodeId ===
                                    code.passwortDatenbankAccessCodeId
                                  }
                                  style={buttonStyle("danger")}
                                >
                                  {revokingCodeId ===
                                  code.passwortDatenbankAccessCodeId
                                    ? "..."
                                    : t("Entziehen")}
                                </button>
                              ) : (
                                <span
                                  style={{ color: "#888", fontSize: "12px" }}
                                >
                                  –
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()
              )}
            </div>
          </Section>

          {/* SECTION 5: Pending Requests */}
          <Section
            id="pending-requests"
            title={t("Anfragen an meine Datenbank")}
            alert={pendingRequestsAlert}
            onDismissAlert={() =>
              setPendingRequestsAlert({ type: null, message: null })
            }
          >
            {pendingRequests.length === 0 ? (
              <p style={{ color: "#666", margin: 0 }}>
                {t("Keine ausstehenden Anfragen.")}
              </p>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>{t("Benutzer (E-Mail)")}</th>
                    <th style={thStyle}>{t("Erstellt am")}</th>
                    <th style={thStyle}>{t("Aktionen")}</th>
                  </tr>
                </thead>
                <tbody>
                  {[...pendingRequests]
                    .sort((a, b) => new Date(b.erstelltAm) - new Date(a.erstelltAm))
                    .map((request) => (
                    <tr key={request.passwortDatenbankBenutzerId}>
                      <td style={tdStyle}>
                        <span style={{ fontSize: "12px" }}>
                          {request.benutzerEmail ||
                            request.benutzerOid ||
                            t("Unbekannt")}
                        </span>
                      </td>
                      <td style={tdStyle}>{formatDate(request.erstelltAm)}</td>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button
                            onClick={() => handleApprove(request)}
                            disabled={
                              savingId === request.passwortDatenbankBenutzerId
                            }
                            style={buttonStyle("success")}
                          >
                            {savingId === request.passwortDatenbankBenutzerId
                              ? "..."
                              : t("Freigeben")}
                          </button>
                          <button
                            onClick={() => handleReject(request)}
                            disabled={
                              savingId === request.passwortDatenbankBenutzerId
                            }
                            style={buttonStyle("danger")}
                          >
                            {t("Entziehen")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>
        </>
      )}

      {/* Alias Modal */}
      {editingAliasId && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => {
            setEditingAliasId(null);
            setNewAliasValue("");
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              padding: "24px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                margin: "0 0 16px 0",
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              {t("Alias vergeben")}
            </h3>
            <div style={{ marginBottom: "16px" }}>
              <label style={labelStyle}>
                {t("Anzeigename (nur für Sie sichtbar)")}
              </label>
              <input
                type="text"
                value={newAliasValue}
                onChange={(e) => setNewAliasValue(e.target.value)}
                placeholder={t("z.B. Team-Tresor")}
                style={inputStyle}
                maxLength={100}
                autoFocus
              />
              <p style={{ fontSize: "11px", color: "#888", marginTop: "4px" }}>
                {t("Leer lassen zum Entfernen des Alias.")}
              </p>
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => {
                  setEditingAliasId(null);
                  setNewAliasValue("");
                }}
                style={buttonStyle("secondary")}
              >
                {t("Abbrechen")}
              </button>
              <button
                onClick={handleSaveAlias}
                disabled={savingId === editingAliasId}
                style={buttonStyle("primary")}
              >
                {savingId === editingAliasId ? t("Speichern...") : t("Speichern")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Sub-components ---

function Section({ title, id, alert, onDismissAlert, children }) {
  return (
    <div
      id={id}
      style={{
        marginBottom: "24px",
        padding: "16px",
        border: "1px solid #ddd",
        borderRadius: "6px",
        backgroundColor: "#fff",
      }}
    >
      <h3
        style={{
          fontSize: "15px",
          fontWeight: "600",
          marginTop: 0,
          marginBottom: "12px",
        }}
      >
        {title}
      </h3>
      {/* Inline Alert */}
      {alert?.message && (
        <div
          style={{
            padding: "10px 12px",
            marginBottom: "12px",
            borderRadius: "4px",
            fontSize: "13px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: alert.type === "success" ? "#d4edda" : "#f8d7da",
            border:
              alert.type === "success"
                ? "1px solid #c3e6cb"
                : "1px solid #f5c6cb",
            color: alert.type === "success" ? "#155724" : "#721c24",
          }}
        >
          <span>{alert.message}</span>
          {onDismissAlert && (
            <button
              onClick={onDismissAlert}
              style={{
                background: "none",
                border: "none",
                fontSize: "16px",
                cursor: "pointer",
                color: alert.type === "success" ? "#155724" : "#721c24",
                padding: "0 4px",
              }}
            >
              ×
            </button>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const { t } = useI18n();
  const colors = {
    Freigegeben: { bg: "#d4edda", text: "#155724" },
    Ausstehend: { bg: "#fff3cd", text: "#856404" },
    Entzogen: { bg: "#f8d7da", text: "#721c24" },
  };
  const c = colors[status] || { bg: "#e9ecef", text: "#495057" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 8px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: "500",
        backgroundColor: c.bg,
        color: c.text,
      }}
    >
      {t(status)}
    </span>
  );
}

function AccessCodeStatusBadge({ status }) {
  const { t } = useI18n();
  const colors = {
    Gültig: { bg: "#d4edda", text: "#155724" },
    Verwendet: { bg: "#cce5ff", text: "#004085" },
    Entzogen: { bg: "#f8d7da", text: "#721c24" },
    Abgelaufen: { bg: "#e9ecef", text: "#495057" },
  };
  const c = colors[status] || { bg: "#e9ecef", text: "#495057" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 8px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: "500",
        backgroundColor: c.bg,
        color: c.text,
      }}
    >
      {t(status)}
    </span>
  );
}

// --- Styles ---

function buttonStyle(variant) {
  const base = {
    padding: "8px 16px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "500",
  };
  switch (variant) {
    case "primary":
      return { ...base, backgroundColor: "#0078d4", color: "#fff" };
    case "secondary":
      return {
        ...base,
        backgroundColor: "#f0f0f0",
        color: "#333",
        border: "1px solid #ccc",
      };
    case "success":
      return {
        ...base,
        backgroundColor: "#28a745",
        color: "#fff",
        padding: "6px 12px",
      };
    case "danger":
      return {
        ...base,
        backgroundColor: "#dc3545",
        color: "#fff",
        padding: "6px 12px",
      };
    case "small":
      return {
        ...base,
        backgroundColor: "#f0f0f0",
        color: "#333",
        border: "1px solid #ccc",
        padding: "4px 10px",
        fontSize: "12px",
        minWidth: "70px",
      };
    case "copied":
      return {
        ...base,
        backgroundColor: "#28a745",
        color: "#fff",
        border: "1px solid #28a745",
        padding: "4px 10px",
        fontSize: "12px",
        cursor: "default",
        minWidth: "70px",
      };
    default:
      return base;
  }
}

function badgeStyle(bg, text) {
  return {
    display: "inline-block",
    padding: "3px 8px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "500",
    backgroundColor: bg,
    color: text,
  };
}

const labelStyle = {
  display: "block",
  fontSize: "13px",
  fontWeight: "500",
  marginBottom: "4px",
  color: "#333",
};

const inputStyle = {
  width: "100%",
  padding: "8px",
  border: "1px solid #ccc",
  borderRadius: "4px",
  fontSize: "13px",
  boxSizing: "border-box",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "13px",
};

const thStyle = {
  textAlign: "left",
  padding: "8px",
  borderBottom: "2px solid #ddd",
  fontSize: "12px",
  fontWeight: "600",
  color: "#555",
};

const tdStyle = {
  padding: "8px",
  borderBottom: "1px solid #eee",
  verticalAlign: "middle",
};

const subTableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "12px",
  backgroundColor: "#fff",
  border: "1px solid #e9ecef",
  borderRadius: "4px",
};

const subThStyle = {
  textAlign: "left",
  padding: "6px 10px",
  borderBottom: "1px solid #e9ecef",
  fontSize: "11px",
  fontWeight: "600",
  color: "#888",
};

const subTdStyle = {
  padding: "6px 10px",
  borderBottom: "1px solid #f0f0f0",
  verticalAlign: "middle",
};

const subThCenterStyle = {
  ...subThStyle,
  textAlign: "center",
};

const subTdCenterStyle = {
  ...subTdStyle,
  textAlign: "center",
};
